// ############################ Require packages here ############################
import React, { lazy, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// ############################ Require files here ###############################
import ScheduleForm from './scheduleForm';
import RelatedResources from './relatedResources';
import './Products/product.scss';
import './ctem.css';
const TestimonialSlider = lazy(() => import('./testimonialSlider'));

gsap.registerPlugin(ScrollTrigger);

const IMG = '/images/ctem/';
const VIDEO = '/videos/intro-video.mp4';

const SUB_NAV = [
    { label: 'Percept CTEM', path: '/sequretek-percept-ctem' },
    { label: 'Percept XDR & NG SIEM', path: '/products/extended-detection-and-response-and-ngsiem' },
    { label: 'Percept Identity', path: '/products/identity-and-access-governance' },
    { label: 'Percept Compliance Manager', path: '/products/compliance-manager' },
];

const PROBLEMS = [
    { icon: 'vision-target11.svg', title: ['Disparate', 'Security View'], text: 'Security tools report their own findings without sharing one inventory, leaving teams to reconcile conflicting asset records across sources.' },
    { icon: 'siren1.svg', title: ['Priority Blind', 'Spots'], text: 'When every finding is flagged as urgent, teams have no clear order for action and no way to connect triage decisions to actual business risk.' },
    { icon: '10-timer.svg', title: ['Remediation', 'Flow Gaps'], text: 'Fixes stall between security and IT when ownership is unclear, handoffs go undocumented, and closure evidence is assumed rather than verified.' },
    { layers: [['flash.svg', 9.84, -0.09, true]], title: ['Speed'], text: 'While AI-enabled attackers uncover and test vulnerabilities within hours, traditional defenders rely on quarterly or annual scans that are obsolete on arrival.' },
    { icon: 'scale-up12.svg', title: ['Scale'], text: 'Attackers chain identity, cloud, SaaS and supplier weaknesses at machine scale while defenders face fragmented, conflicting inventories without a trusted view.' },
    { layers: [['scale-balance.svg', -0.4, 3.09]], title: ['Asymmetry'], text: 'Attackers need only one gap, quickly turning patches into exploits within days, while defenders must close every gap in sequence and provide proof of closure.' },
];

const LOOP_STEPS = [
    { icon: 'group.svg', title: 'Scoping', w: 204, text: 'Define scope, asset criticality, ownership, and compliance priorities that matter.' },
    { icon: 'search51.svg', title: 'Discovery', w: 200, text: 'Establish one asset inventory and identify assets missing from registered sources.' },
    { icon: 'prioritize.svg', title: 'Prioritization', w: 192, text: 'Create a risk score based on criticality, exposure, threat, identity, and compliance.' },
    { icon: 'validation.svg', title: 'Validation', w: 218, text: 'Distinguish a reported workflow change from evidence that an exposure has been fixed.' },
    { icon: 'mobilization.svg', title: 'Mobilization', w: 192, text: 'Coordinate action across teams, track progress, and feed outcomes into the next cycle.' },
];

const ADVANTAGES = [
    { icon: 'chart-connected1.svg', title: 'Coexists, Complements, and Consolidates', text: 'Percept CTEM sends prioritized work into patching, SOAR, and ITSM, and rescores as ticket status changes. You do not have to replace your tech stack to start.' },
    { icon: 'group1.svg', title: 'One Inventory, Before Any Score', text: 'Percept CTEM reconciles seven asset sources into one inventory, then names the blind spots by source, including rogue assets found only by unauthenticated scans.' },
    { icon: 'vector.svg', title: 'Business Risk, Not a CVSS Queue', text: 'The Asset Security Score weighs criticality, exposure, threat, identity, and compliance. The Enterprise Risk Score traces back to the contributing hosts.' },
    { icon: 'memo-circle-check11.svg', title: 'Exposure and Compliance on One Record', text: 'Policy gaps and audit evidence sit on the same assets as exposures. Security and compliance work from one record, not two versions of the truth.' },
];

const NOT_ITEMS = [
    { icon: 'not-api.svg', title: ['Not vulnerability', 'management with extras'], small: true, text: 'VM ranks CVEs on hosts it can already see, by CVSS.', missing: 'Inventory reconciliation, identity, and compliance context.' },
    { layers: [['not-bar1.svg', 0, 4], ['not-bar2.svg', 3, 18.5]], title: ['Not a SIEM', 'dashboard'], text: 'SIEM correlates events that have already happened.', missing: 'Internal estate, control coverage, and rogue assets inside.' },
    { icon: 'not-it-computer.svg', title: ['Not breach & attack simulation'], text: 'BAS tests whether known techniques get past the controls you have.', missing: 'Continuous discovery, estate-wide prioritization, and ownership of fix.' },
    { layers: [['not-external-link.svg', 4, 4]], title: ['Not external', 'attack surface management'], text: 'ASM maps what is reachable from the internet, outside in.', missing: 'Exposure is what has not happened yet and what to close first.' },
];

// Blogs are fixed in-app routes; case studies come from the API (matched by title) because their pages are data-driven.
const resources = [
    {
        kind: 'case-study',
        img: 'case-studies.webp',
        label: 'Case Study',
        icon: '/images/case-study-icon.svg',
        title: 'Treating Vulnerabilities Before They Become Symptoms',
        path: '/resources/case-study/Treating%20Vulnerabilities%20Before%20They%20Become%20Symptoms'
    },
    {
        kind: 'blog',
        img: 'beyond-the-numbers.webp',
        label: 'Blog',
        icon: '/images/blog-icon.svg',
        title: 'Beyond the Numbers: How Context-Driven Risk Scores Empower Your Team',
        path: '/resources/blog/Beyond-the-Numbers'
    },
    {
        kind: 'blog',
        img: 'every-asset-matters.webp',
        label: 'Blog',
        icon: '/images/blog-icon.svg',
        title: 'Every Asset Matters',
        path: '/resources/blog/Every-Asset-Matters'
    },
      {
        kind: 'case-study',
        img: 'case-studies.webp',
        label: 'Case Study',
        icon: '/images/case-study-icon.svg',
        title: 'Bringing Cyber Risk into Sharp Focus',
        path: '/resources/case-study/Bringing%20Cyber%20Risk%20into%20Sharp%20Focus'
    },
];

const FEATURES = [
    'Homogenized asset inventory', 'Adversarial exposure validation', 'AI remediation playbooks',
    'Breach & attack simulation', 'Rogue asset discovery & regularization', 'Asset criticality grading',
    'Vulnerability & misconfiguration detection', 'Compliance audit reporting', 'Asset threat mapping & attack paths',
    'Agentic AI risk scoring', 'Patch management & ticketing', 'Connector Studio integrations',
];

const SOLUTIONS = [
    { title: 'PERCEPT XDR & NG SIEM', text: 'Cloud-based AI-powered solution for 24/7 security monitoring, threat detection, and automated incident response for your IT', path: '/products/extended-detection-and-response-and-ngsiem' },
    { title: 'PERCEPT IDENTITY', text: 'Zero Trust identity and access orchestration, governance. Meet compliance needs and cover access risks across environments', path: '/products/identity-and-access-governance' },
    { title: 'PERCEPT COMPLIANCE MANAGER', text: 'Continuous governance, risk, compliance across environments with vulnerability assessment and configuration audits on a single console', path: '/products/compliance-manager' },
    { title: 'PERCEPT SERVICES', text: 'Expert-led MSS with a 24/7 MDR SOC for continuous monitoring, threat hunting, and incident response across your enterprise.', path: '/services' },
];

const FAQS = [
    { q: 'What is continuous threat exposure management?', a: 'Continuous Threat Exposure Management is a repeating five-stage program for finding, prioritizing, validating, and coordinating action on exposures. Sequretek’s Percept CTEM runs Scoping, Discovery, Prioritization, Validation, and Mobilization on one asset inventory.' },
    { q: 'How is Percept CTEM different from vulnerability management?', a: 'Sequretek Percept CTEM is a business-risk-led platform that extends beyond identifying and fixing vulnerabilities. It combines asset scope, criticality, threat, identity, and compliance context with evidence review and cross-team action.' },
    { q: 'Does Percept CTEM replace our SIEM?', a: 'No, Sequretek Percept CTEM works with your existing SIEM. SIEM data contributes context to its asset-led view of exposure, risk, and remediation.' },
    { q: 'How fast can we start?', a: 'A starting scope for Sequretek Percept CTEM is one asset set and one exposure-management priority. There is no universal timeline. Timing depends on source availability, asset data quality, and workflow readiness.' },
];

// Icons that Figma builds from several layers are stacked in a 48px box at their design offsets.
const Icon = ({ icon, layers }) => (icon
    ? <img src={`${IMG}${icon}`} alt='' width='48' height='48' />
    : (
        <span className='ctem-ico' aria-hidden='true'>
            {layers.map(([src, left, top, flip]) => (
                <img key={src} src={`${IMG}${src}`} alt='' className={flip ? 'ctem-ico--flip' : ''} style={{ left, top }} />
            ))}
        </span>
    ));

const PerceptCtem = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState(0);
    const videoFrameRef = useRef(null);
    const loopSecRef = useRef(null);
    const loopViewportRef = useRef(null);
    const loopTrackRef = useRef(null);

    useEffect(() => {
        const mm = gsap.matchMedia();

        // Pinned loop: the section holds while the stage cards travel sideways.
        mm.add('(min-width: 992px)', () => {
            const loopSec = loopSecRef.current;
            const viewport = loopViewportRef.current;
            const track = loopTrackRef.current;
            if (!loopSec || !viewport || !track) return;

            const distance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
            gsap.to(track, {
                x: () => -distance(),
                ease: 'none',
                scrollTrigger: {
                    trigger: loopSec,
                    start: 'top top',
                    end: () => `+=${distance() + 200}`,
                    pin: true,
                    scrub: 0.6,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                },
            });
        });

        // Video (same motion as terrahq.com): starts small at 0.417 from the top-centre and scales to full size as it scrolls in. Not pinned.
        mm.add('all', () => {
            const frame = videoFrameRef.current;
            if (!frame) return;
            gsap.fromTo(
                frame,
                { scale: 0.417, transformOrigin: 'center top' },
                {
                    scale: 1,
                    duration: 1,
                    ease: 'none',
                    scrollTrigger: { trigger: frame, start: 'top 90%', end: 'bottom 50%', scrub: 1 },
                },
            );
        });

        // Images above the pins change page height, so re-measure once they load.
        const refresh = () => ScrollTrigger.refresh();
        window.addEventListener('load', refresh);
        const t = setTimeout(refresh, 600);

        return () => {
            clearTimeout(t);
            window.removeEventListener('load', refresh);
            mm.revert();
        };
    }, []);

    const scrollToLoop = () => {
        const el = loopSecRef.current;
        if (!el) return;
        // Pinned sections shift offsets, so read the pin-spacer's position instead.
        const top = (el.parentElement?.classList.contains('pin-spacer') ? el.parentElement : el).getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    return (
        <div className='ctem-page seq-Percept-page'>
            <Helmet>
                <title>Continuous Threat Exposure Management | Sequretek Percept</title>
                <meta http-equiv="X-Frame-Options" content="DENY" />
                <meta name="description" content="Continuous Threat Exposure Management that ranks fixes by business risk. Percept CTEM runs five stages, one loop: scope, discover, prioritize, validate, mobilize. See how it works." />
                <meta name="keywords" content="continuous threat exposure management, CTEM, business risk prioritization, risk score, exposure management platform, compliance management, asset inventory reconciliation, shadow IT, rogue asset discovery, compliance reporting, exposure management, NIST CSF, SEBI CSCRF, RBI, automated remediation, AI-driven attacks, attack chaining" />
            </Helmet>

            {/* HERO */}
            <section className='ctem-hero' style={{ backgroundImage: `url(${IMG}image24943.png)` }}>
                <div className='ctem-hero__overlay' />
                <div className='ctem-container ctem-hero__content'>
                    <h1>Every Asset. Every Moment. No Surprises.</h1>
                    <p>Percept CTEM helps you know what you own, prioritize what puts the business at risk, and coordinate action.</p>
                    <div className='ctem-hero__actions'>
                        <button type='button' className='ctem-btn ctem-btn--red' onClick={scrollToLoop}>See the Percept CTEM Loop</button>
                        <button type='button' className='ctem-btn ctem-btn--outline' onClick={() => { window.location.href = 'https://www.sequretek.com/resources/datasheet/Percept%20CTEM%20Datasheet'; }}>Download Datasheet</button>
                    </div>
                </div>
            </section>

            {/* SUB NAV */}
            <nav className='ctem-subnav'>
                {SUB_NAV.map((item, i) => (
                    <button type='button' key={item.label} className={i === 0 ? 'active' : ''} onClick={() => { window.scrollTo(0, 0); navigate(item.path); }}>
                        {item.label}
                    </button>
                ))}
            </nav>

            {/* VIDEO - scales up on scroll (not pinned) */}
            <div className='ctem-pin-wrap'>
                <section className='ctem-video'>
                    <div className='ctem-video__wrap'>
                        <div className='ctem-video__frame' ref={videoFrameRef}>
                            <video
                                src={VIDEO}
                                ref={(el) => { if (el) { el.muted = true; el.play().catch(() => {}); } }}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload='auto'
                            />
                        </div>
                    </div>
                </section>
            </div>

            {/* THE PROBLEM */}
            <section className='ctem-section ctem-problem'>
                <div className='ctem-container'>
                    <p className='ctem-eyebrow ctem-eyebrow--center'>The Problem</p>
                    <h2 className='ctem-h2 ctem-center'>Where Security Teams Lose Momentum </h2>
                    <p className='ctem-lead ctem-center ctem-problem__lead'>
                        The conflicting inventories, flat priorities and stalled fixes are exactly what{' '}<br />
                        AI-assisted attackers exploit first, faster than quarterly security cycles can respond.
                    </p>
                    <div className='ctem-problem__grid'>
                        {PROBLEMS.map((p) => (
                            <div className='ctem-panel' key={p.title.join(' ')}>
                                <Icon icon={p.icon} layers={p.layers} />
                                <h3>{p.title[0]}{p.title[1] && <><br />{p.title[1]}</>}</h3>
                                <p>{p.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* THE LOOP - pinned, cards scroll sideways */}
            <div className='ctem-pin-wrap'>
            <section className='ctem-loop' id='ctem-loop' ref={loopSecRef}>
                <div className='ctem-loop__intro'>
                    <p className='ctem-eyebrow'>The Percept CTEM Loop</p>
                    <h2 className='ctem-h2'>What Continuous Threat Exposure Management Means</h2>
                    <p className='ctem-loop__lead'>
                        Percept CTEM is a continuous exposure management platform that builds one asset inventory, and ranks fixes by business risk. It repeats five stages—scoping, discovery, prioritization, validation, mobilization—as assets, threats, and priorities change. 
                    </p>
                    <button type='button' className='ctem-btn ctem-btn--red-outline' onClick={() => navigate('/schedule-a-demo', { state: { scrollToForm: true } })}>Start a Conversation</button>
                </div>
                <div className='ctem-loop__viewport' ref={loopViewportRef}>
                    <div className='ctem-loop__track' ref={loopTrackRef}>
                        {LOOP_STEPS.map((s) => (
                            <div className='ctem-card ctem-card--loop' key={s.title}>
                                <img src={`${IMG}${s.icon}`} alt='' width='48' height='48' />
                                <h3>{s.title}</h3>
                                <p style={{ width: s.w }}>{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            </div>

            {/* ADVANTAGE */}
            <section className='ctem-section ctem-advantage'>
                <div className='ctem-container'>
                    <div className='ctem-advantage__head'>
                        <div>
                            <p className='ctem-eyebrow'>The Percept CTEM Advantage</p>
                            <h2 className='ctem-h2'>How Percept CTEM Is{' '}<br />Different from Other Tools</h2>
                        </div>
                        <p className='ctem-lead'>
                            Percept CTEM brings Exposure Management and Continuous Compliance together on one asset inventory, one risk register, one AI engine, and one evidence store, while working with your existing security stack.
                        </p>
                    </div>
                    <div className='ctem-advantage__grid'>
                        {ADVANTAGES.map((a) => (
                            <div className='ctem-panel ctem-panel--tall' key={a.title}>
                                <img src={`${IMG}${a.icon}`} alt='' />
                                <h3>{a.title}</h3>
                                <p>{a.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT PERCEPT CTEM IS NOT */}
            <section className='ctem-not' style={{ '--bg-img': `url(${IMG}image24975.png)` }}>
                <div className='ctem-not__overlay' />
                <div className='ctem-container ctem-not__content'>
                    <h2 className='ctem-h2 ctem-center'>What Percept CTEM Is Not</h2>
                    <p className='ctem-lead ctem-center'>
                        Security teams already run scanners, attack-surface tools, control tests, and a SIEM. CTEM is not a new name for any of them. It is the continuous loop that turns those signals into one fix order for enterprises.
                    </p>
                    <div className='ctem-not__grid'>
                        {NOT_ITEMS.map((n) => (
                            <div className='ctem-ncard' key={n.text}>
                                <div className='ctem-ncard__body'>
                                    <Icon icon={n.icon} layers={n.layers} />
                                    <h3 className={n.small ? 'ctem-ncard__title--sm' : ''}>{n.title[0]}{n.title[1] && <>{' '}<br />{n.title[1]}</>}</h3>
                                    <p>{n.text}</p>
                                </div>
                                <img className='ctem-ncard__line' src={`${IMG}not-line.svg`} alt='' />
                                <p className='ctem-ncard__missing'><span>Missing piece:</span> {n.missing}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURES + CTA */}
            <div className='ctem-features-wrap'>
            <section className='ctem-section ctem-features'>
                <div className='ctem-container'>
                    <p className='ctem-eyebrow ctem-eyebrow--center'>FEATURES</p>
                    <h2 className='ctem-h2 ctem-center ctem-features__title'>
                        Unify asset discovery, business-risk prioritization, remediation, and continuous compliance with Percept CTEM.
                    </h2>
                    <div className='ctem-features__shot'>
                        <img src={`${IMG}edr-dashboard.png`} alt='Percept CTEM dashboard' />
                    </div>
                    <div className='ctem-features__chips'>
                        {FEATURES.map((f) => (
                            <div className='ctem-chip' key={f}>
                                <img src={`${IMG}shield-plus1.svg`} alt='' width='24' height='24' />
                                <p>{f}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA BANNER */}
            <section className='ctem-cta-wrap'>
                <div className='ctem-container'>
                    <div className='ctem-cta' style={{ backgroundImage: `url(${IMG}rectangle1391.png)` }}>
                        <div className='ctem-cta__overlay' />
                        <div className='ctem-logo-mark ctem-logo-mark--cta' aria-hidden='true'><img src={`${IMG}image24954.png`} alt='' /></div>
                        <div className='ctem-cta__content'>
                            <h2>Bring your asset sources. Leave with a priority.</h2>
                            <p>A Sequretek Percept CTEM working session maps your asset sources to one exposure-management priority. Bring your source list to discuss what to prioritise and who will own the next action.</p>
                            <button type='button' className='ctem-btn ctem-btn--red' onClick={() => { window.scrollTo(0, 0); navigate('/schedule-a-demo'); }}>Book a Technical Session</button>
                        </div>
                    </div>
                </div>
            </section>
            </div>

            {/* FAQ */}
            <section className='ctem-faq'>
                <div className='ctem-container ctem-faq__inner'>
                    <p className='ctem-eyebrow ctem-eyebrow--center'>FAQs</p>
                    <h2 className='ctem-h2 ctem-center'>Frequently Asked Questions</h2>
                    <div className='ctem-faq__list'>
                        {FAQS.map((f, i) => (
                            <div className={`ctem-faq__item${openFaq === i ? ' open' : ''}`} key={f.q}>
                                <button type='button' className='ctem-faq__q' aria-expanded={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                                    <span>{f.q}</span>
                                    <img src={`${IMG}angle-circle-down21.svg`} alt='' width='32' height='32' />
                                </button>
                                {openFaq === i && <p className='ctem-faq__a'>{f.a}</p>}
                            </div>
                        ))}
                    </div>
                    <div className='ctem-faq__contact'>
                        <div>
                            <h3>Still have Questions?</h3>
                            <p>Our team is happy to walk you through Percept CTEM.</p>
                        </div>
                        <button type='button' className='ctem-btn ctem-btn--red' onClick={() => { window.scrollTo(0, 0); navigate('/contactus'); }}>Contact Us</button>
                    </div>
                </div>
            </section>

            {/* EXPLORE SOLUTIONS */}
            <section className='ctem-solutions' style={{ '--bg-img': `url(${IMG}rectangle1344.png)` }}>
                <div className='ctem-solutions__overlay' />
                <div className='ctem-logo-mark ctem-logo-mark--solutions' aria-hidden='true'><img src={`${IMG}image24932.png`} alt='' /></div>
                <div className='ctem-container ctem-solutions__content'>
                    <h2 className='ctem-h2'>Explore our Solutions</h2>
                    <p className='ctem-lead'>Enterprise cybersecurity platform and products that help you maintain a proactive and comprehensive security posture against all threats</p>
                    <div className='ctem-solutions__grid'>
                        {SOLUTIONS.map((s) => (
                            <div className='ctem-solutions__item' key={s.title}>
                                <h4>{s.title}</h4>
                                <p>{s.text}</p>
                                <button type='button' className='ctem-btn ctem-btn--red' onClick={() => { window.scrollTo(0, 0); navigate(s.path); }}>Explore</button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <div className='seq-quotes'>
                {/* <div className='gredient'> </div> */}
                <TestimonialSlider page={'platform'} />
            </div>

            {/* SCHEDULE FORM */}
            <div className='seq-forms'>
                <div className='container'>
                    <div className='insurance-partseven'>
                        {window.innerWidth > 767 ? <h2 className='insurance-partseven-heading'>How can we help improve your security posture? Let’s talk!</h2> : null}
                        <ScheduleForm
                            bg={'/images/bg-img22.svg'}
                            mobilebg={'/images/light-umbrella.svg'}
                            page='percept-platform'
                            text={'With businesses being targeted by cyberthreats every single day, we urge you not to leave your business vulnerable. Arm your enterprise with robust cybersecurity solutions that not only defend against attacks but also empower your growth.'}
                        />
                    </div>
                </div>
            </div>

            {/* RELATED RESOURCES */}
            <div className='resources ctem-resources'>
                <div className='container'>
                    <h4 className='ctem-resources__title'>Related resources</h4>
                    <div className="row">
                        <RelatedResources
                            type='case-study'
                            renderCards={(list, open) => resources.map((item) => {
                                const target = item.kind === 'case-study' ? list?.find((r) => r.title === item.title) : null;
                                const href = item.kind === 'blog'
                                    ? item.path
                                    : `/resources/case-study/${encodeURIComponent(target ? target.title : item.title)}`;
                                const onClick = (e) => {
                                    e.preventDefault();
                                    if (item.kind === 'case-study' && target) open(e, target);
                                    else { window.scrollTo(0, 0); navigate(href); }
                                };
                                return (
                                    <div className='col-lg-3 col-sm-6 col-12' key={item.title}>
                                        <a href={href} className='rsource-card' onClick={onClick}>
                                            <div className='resource-img'>
                                                <img src={`${IMG}${item.img}`} alt='' />
                                                <div className="rs-badge"><img src={item.icon} alt='' /> <span>{item.label}</span></div>
                                            </div>
                                            <div className='resource-content'>
                                                <h5>{item.title}</h5>
                                            </div>
                                        </a>
                                    </div>
                                );
                            })}                            
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}; 

export default PerceptCtem;
